import { Component } from 'preact';
import { motion } from 'framer-motion';
import { cn } from '../lib/utils';
import { playUI, UI } from '../utils/arena/uiSounds';
import { getSeasonLevel, getNextTierInfo, canClaimTier, getTimeRemaining } from '../utils/arena/seasonPass';
import RuneSpinner from './RuneSpinner';
import DeckCardTile from './DeckCardTile';
import CardInspector, { RARITY_COLORS } from './CardInspector';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import AppHeader from './AppHeader';
import { BackButton } from './ui/navButtons';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, COIN_COLOR,
  BEVELED_BTN, VIGNETTE, PANEL_BG, CONTENT_BG_DEFAULT,
  CornerPlating, FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { screenPanel } from '../utils/screenEnter';
import VikingOrnament from './VikingOrnament';
import AmbientParticles from './AmbientParticles';
import { CoinIcon, ShardIcon, KeystoneIcon } from './ui/icons';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';
import { getRankedSeasonSelf } from '../utils/arena/rankedSeasonApi';
import { resolvePrinting } from '../utils/sorcery/foil';
import { canRerollTrials, maxFerocity, QUEST_REROLL_COST, FOIL_REROLL_COST } from '../utils/arena/battlemage';
import { getClass } from '../utils/arena/classes';
import ForgedButton from './ui/ForgedButton';
import { FerocityGauge } from './play/BattlemageGauges';
import { t } from '../localization/t';

const BATTLEMAGE_ACCENT = getClass('battlemage').accent;

// Tiers that render as a foil card (3, 5, 7 are Ord/Exc foils, 10 is
// the Elite capstone).
const FOIL_TIERS = new Set([3, 5, 7, 10]);
function isFoilTier(tier) {
  return FOIL_TIERS.has(tier.level);
}

const TRIALS_TUTORIAL_KEY = 'arcane-trials';

// The reward track's foil tiles each spin up a WebGL context, compile a
// shader and upload textures once their art has decoded. Doing that during
// the entrance animation stuttered the fade, so the tiles show their static
// rest frame first and go live once the entrance has settled (DeckCardTile
// hands off seamlessly: both share the uTilt=0 rest frame).
const FOIL_LIVE_DELAY_MS = 600;

const trialsTutorialSteps = () => [
  {
    key: 'welcome',
    title: t('Welcome to the Arcane Trials'),
    body: t("The Arcane Trials is Valkenhall's seasonal progression pass. Every cycle runs for two weeks and rotates through a featured set, bringing fresh foil rewards and a fresh quest pool. Play matches, finish quests, climb the track: that's the whole loop."),
  },
  {
    key: 'xp-summary',
    title: t('Season XP'),
    body: t("Your total Season XP and current tier sit in the header. Every match you play awards Season XP automatically, on top of the gold and match XP you already earn. The bigger number here means you're closer to the next tier reward."),
    selector: '[data-tutorial="trials-xp-summary"]',
  },
  {
    key: 'reward-track',
    title: t('The Reward Track'),
    body: t("Ten tiers of rewards sit along the progress bar. Each tier unlocks as you cross its XP threshold: gold and Arcana Shards on even tiers, and foil cards on tiers 3, 5, 7, and 10. The featured set rotates every cycle, so each season's foils come from a different pool."),
    selector: '[data-tutorial="trials-reward-track"]',
  },
  {
    key: 'claiming',
    title: t('Claim Your Rewards'),
    body: t("When a tier is unlocked, its reward medallion glows gold; click it to claim. Gold and shards drop into your wallet, foil cards join your collection, and the claimed tier dims to grey so you can see at a glance what's left to collect."),
  },
  {
    key: 'quests',
    title: t('Active Quests'),
    body: t('Three quests are always active at the bottom of the screen, things like "win 5 matches", "win with a Water deck", or "open 3 boosters." Completing a quest drops a chunk of bonus Season XP, and a new quest rotates in from the season pool to replace it.'),
    selector: '[data-tutorial="trials-quests"]',
  },
  {
    key: 'fair-play',
    title: t('Play For the Journey'),
    body: t('You earn Season XP for every match you play, win or lose, so you progress just by showing up and having a good time. The pass is about enjoying the rhythm of a season, not grinding a leaderboard. When the cycle ends, a fresh set of rewards rolls in and the journey starts again.'),
  },
];

export default class ArcaneTrials extends Component {
  constructor(props) {
    super(props);
    this.state = {
      claiming: null,
      // The Glory: which re-roll is in flight ('quest-0', 'foil-3'), or null.
      rerolling: null,
      hoveredCard: null,
      inspectedEntry: null,
      showTrialsTutorial: false,
      // Ranked-season stats shown inline in the header next to Season
      // XP. Pulled on mount + refreshed when the Ranked overlay closes
      // so the header reflects whatever points the player just earned.
      rankedSelf: null,
      // See FOIL_LIVE_DELAY_MS.
      foilLive: false,
    };
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);

    // First-run onboarding, deferred so the reward track + quests
    // have laid out before the overlay measures its targets. Waits
    // on hydration so the on-disk seen flag is read first.
    const profileId = this.props.profile?.id;
    if (profileId) {
      hydrateTutorialState().then(() => {
        if (this._unmounted) return;
        if (!shouldAutoPlay(profileId, TRIALS_TUTORIAL_KEY)) return;
        this.startTutorialTimer();
      });
    }

    this.fetchRankedSelf();
    this._foilLiveTimer = setTimeout(() => {
      if (!this._unmounted) this.setState({ foilLive: true });
    }, FOIL_LIVE_DELAY_MS);
  }

  startTutorialTimer() {
    if (this._tutorialTimer) return;
    this._tutorialTimer = setTimeout(() => {
      if (!this._unmounted) this.setState({ showTrialsTutorial: true });
    }, 600);
  }

  // Resolve the foil card a foil tier pays out. Null when the card or
  // its FOIL printing can't be resolved from a stale season payload.
  // Those tiers render a placeholder tile.
  resolveFoilReward(reward) {
    const foilId = reward.foilCardId;
    const printingId = reward.foilPrintingId;
    const card = this.props.sorceryCards?.find((c) => c.unique_id === foilId);
    // These tiers are foil rewards by definition, so a missing/stale
    // foilPrintingId must fall back to the card's FOIL printing, not printings[0].
    const printing = resolvePrinting(card, printingId, 'F');
    if (!card || !printing) return null;
    return { card, printing };
  }

  fetchRankedSelf = async () => {
    try {
      const rankedSelf = await getRankedSeasonSelf();
      if (this._unmounted) return;
      this.setState({ rankedSelf });
    } catch (err) {
      // Old beta server without /season-ranked/me. Silently leave
      // rankedSelf null. The header pill renders a placeholder state
      // ("Not qualified") which is still a valid CTA into the overlay.
      console.warn('[ArcaneTrials] ranked self fetch failed:', err);
    }
  };

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.hoveredCard &&
      this.state.hoveredCard !== prevState.hoveredCard &&
      this.inspectorHold.consumePending()
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedEntry: this.state.hoveredCard });
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
    if (this._foilLiveTimer) clearTimeout(this._foilLiveTimer);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    removeDismiss(this._escDismissId);
  }

  handleTrialsTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, TRIALS_TUTORIAL_KEY);
    this.setState({ showTrialsTutorial: false });
  };

  handleOpenRankedSeason = () => {
    playUI(UI.CLICK);
    this.props.onOpenSeasonRanking?.();
  };

  handleKeyDown = (e) => {
    if ((e.key === ' ' || e.code === 'Space') && !e.target?.isContentEditable && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName)) {
      e.preventDefault();
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) return;
      if (this.state.inspectedEntry) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredCard) {
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: this.state.hoveredCard });
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  handleClaim = async (level) => {
    this.setState({ claiming: level });
    try {
      await this.props.onClaimReward(level);
    } finally {
      this.setState({ claiming: null });
    }
  };

  // The Glory: a Battlemage re-rolls quests and foil tiers for Ferocity.
  // Null for everyone else, so no re-roll control ever mounts for them.
  get trials() {
    const { profile } = this.props;
    if (!canRerollTrials(profile?.skills)) return null;
    return { ferocity: profile?.battlemage?.ferocity || 0, max: maxFerocity(profile.skills) };
  }

  handleRerollQuest = async (slot) => {
    this.setState({ rerolling: `quest-${slot}` });
    try {
      await this.props.onRerollQuest(slot);
    } finally {
      this.setState({ rerolling: null });
    }
  };

  handleRerollFoil = async (level) => {
    this.setState({ rerolling: `foil-${level}` });
    try {
      await this.props.onRerollFoil(level);
    } finally {
      this.setState({ rerolling: null });
    }
  };

  // A re-rolled quest's bonuses (An Unexpected Request, New-found Momentum).
  renderQuestTags(quest, compact) {
    if (!quest.doubled && !quest.rushReward) return null;
    const tag = (text) => (
      <span
        key={text}
        className={cn('font-bold arena-heading', compact ? 'text-[0.5cqw]' : 'text-[0.5625cqw]')}
        style={{ color: BATTLEMAGE_ACCENT, padding: '0.125cqw 0.5cqw', borderRadius: 'var(--radius-sm)', boxShadow: `0 0 0 1px ${BATTLEMAGE_ACCENT}88`, background: `${BATTLEMAGE_ACCENT}1a` }}
      >
        {text}
      </span>
    );
    return (
      <div className="flex items-center gap-1.5 mb-2">
        {quest.doubled ? tag(t('Doubled rewards')) : null}
        {quest.rushReward ? tag(t('+1 Rush of Battle')) : null}
      </div>
    );
  }

  // Quest card renderer. The quest object is the server-attached
  // shape from /me/season's activeQuests array. Already a flat
  // { id, name, description, target, xpReward, progress, completed }
  // record. No template lookup needed.
  //
  // `compact` is set when the player has the skill-tree perk that
  // doubles the active quest pool from 3 to 6. In that mode we render
  // a slimmer card so all six fit on a single row of the footer grid
  // without pushing the ornamental divider up over the progress bar.
  renderQuestCard(quest, compact = false) {
    if (!quest) return null;
    const progressPct = Math.min(100, ((quest.progress || 0) / Math.max(1, quest.target)) * 100);
    const completed = !!quest.completed;
    const trials = this.trials;
    const rerolling = this.state.rerolling != null;
    return (
      <div
        key={quest.id}
        className={cn('relative flex flex-col rounded-lg', compact ? 'p-2.5' : 'p-4')}
        style={{
          background: PANEL_BG,
          border: `1px solid ${GOLD} 0.2)`,
          opacity: completed ? 0.65 : 1,
        }}
      >
        <CornerPlating position="top-left" />
        <CornerPlating position="top-right" />
        <CornerPlating position="bottom-left" />
        <CornerPlating position="bottom-right" />

        <div
          className={cn('font-bold arena-heading mb-1', compact ? 'text-xs' : 'text-sm')}
          style={{ color: TEXT_PRIMARY }}
        >
          {quest.name}
        </div>
        <div
          className={cn(compact ? 'text-[0.5625cqw] mb-2' : 'text-xs mb-3')}
          style={{ color: TEXT_BODY }}
        >
          {quest.description}
        </div>
        {this.renderQuestTags(quest, compact)}

        <div className={cn('relative rounded-full overflow-hidden mb-2', compact ? 'h-2' : 'h-3')} style={{
          background: 'rgba(0,0,0,0.4)',
          border: `1px solid ${GOLD} 0.15)`,
        }}>
          <div className="absolute inset-0 rounded-full" style={{
            width: `${progressPct}%`,
            background: completed
              ? 'linear-gradient(90deg, #6b8d3a, #b6d65a)'
              : 'linear-gradient(90deg, #8b6914, #d4a843)',
            transition: 'width 0.4s ease-out',
          }} />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs" style={{ color: TEXT_MUTED }}>
            {completed ? t('Completed') : `${quest.progress || 0} / ${quest.target}`}
          </div>
          <div className="flex items-center gap-2 text-xs font-bold" style={{ color: ACCENT_GOLD }}>
            <span>{t('+{n} XP', { n: quest.xpReward })}</span>
            {quest.keystoneReward > 0 && (
              <span className="flex items-center gap-1">
                +{quest.keystoneReward}
                <KeystoneIcon size={12} />
              </span>
            )}
          </div>
        </div>
        {trials && !completed ? (
          <div className="flex justify-end mt-2">
            <ForgedButton
              className={cn('whitespace-nowrap', compact ? 'px-2 py-0.5 text-[0.5cqw]' : 'px-3 py-1 text-[0.625cqw]')}
              disabled={rerolling || trials.ferocity < QUEST_REROLL_COST}
              onClick={() => this.handleRerollQuest(quest.slot)}
            >
              {t('Re-roll ({n} Ferocity)', { n: QUEST_REROLL_COST })}
            </ForgedButton>
          </div>
        ) : null}
      </div>
    );
  }

  // Small dark + gold + recessed checkmark badge used to mark a tier
  // reward as already claimed. Anchored to the top-right corner of its
  // positioning ancestor. Used by both the foil card branch (anchored
  // to the DeckCardTile) and the medallion branch (anchored to the
  // 60×60 button wrapper).
  renderClaimedCheck() {
    return (
      <div
        className="absolute flex items-center justify-center z-10"
        style={{
          width: '1.375cqw',
          height: '1.375cqw',
          top: -4,
          right: -4,
          borderRadius: '50%',
          background: 'linear-gradient(180deg, rgba(8,6,4,0.97) 0%, rgba(20,15,6,0.97) 100%)',
          border: `1.5px solid ${ACCENT_GOLD}`,
          // Inset shadow on top + warm hint on bottom = engraved feel.
          // Outer drop-shadow grounds it on the panel.
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.7), inset 0 -1px 0 rgba(255,220,140,0.18), 0 1px 4px rgba(0,0,0,0.5)',
          color: ACCENT_GOLD,
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" style={{ width: '0.6875cqw', height: '0.6875cqw', filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.6))' }}>
          <path d="M5 12 L10 17 L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  }

  // The season payload arrives asynchronously, and a brand-new account's
  // server-side season_progress defaults to '{}' (Postgres JSONB default),
  // so the shape is checked too, not just truthiness.
  hasSeasonData() {
    const { season, progress } = this.props;
    return !!season && !!progress && Array.isArray(progress.claimedTiers);
  }

  render() {
    const {
      season, progress, onBack, profile,
      onToggleMailbox, mailboxUnreadCount, mailboxDropdown,
      onToggleFriends, friendListData,
    } = this.props;

    // Always render the AppHeader so the Back button is reachable
    // while season / progress are still loading. Without this the view
    // is an inescapable fullscreen spinner if loadCurrentSeason fails
    // (network blip, brand-new account with a transient error). The
    // only escape was force-quitting the app.
    //
    // Checking for `claimedTiers` rather than just truthy `progress`:
    // a brand-new account's server-side season_progress defaults to
    // '{}' (Postgres JSONB default). Without this shape check the
    // render would proceed and throw on progress.claimedTiers.includes,
    // unmounting the tree to a blank background.
    if (!this.hasSeasonData()) {
      return (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#08080a' }}>
          <AppHeader
            profile={profile}
            onToggleMailbox={onToggleMailbox}
            mailboxUnreadCount={mailboxUnreadCount}
            mailboxDropdown={mailboxDropdown}
            draftQueueDropdown={this.props.draftQueueDropdown}
            onToggleFriends={onToggleFriends}
            friendListData={friendListData}
          >
            <BackButton title={t('Back to Hub')} onClick={onBack} />
          </AppHeader>
          <div className="flex-1 flex items-center justify-center">
            <RuneSpinner size={48} />
          </div>
        </div>
      );
    }

    const { seasonXp } = progress;
    const currentLevel = getSeasonLevel(seasonXp, season.tiers);
    const nextTier = getNextTierInfo(seasonXp, season.tiers);
    const timeLeft = getTimeRemaining(season.endsAt);
    const maxXp = season.tiers[season.tiers.length - 1].xpRequired;
    const activeQuests = season.activeQuests || [];
    const questsCompact = activeQuests.length > 3;

    return (
      <div className="fixed inset-0 z-50 flex flex-col" style={{
        background: `url("/deck-builder-bg-dimmed.webp") center no-repeat, #08080a`,
        backgroundSize: '100% 100%',
      }}>
        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <AmbientParticles preset="deckbuilder" />

        {/* Unified app header. Back + Season XP summary on the left,
            shared gold/shards/mail/friends slot on the right. */}
        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          draftQueueDropdown={this.props.draftQueueDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
        >
          <BackButton title={t('Back to Hub')} onClick={onBack} />
        </AppHeader>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full overflow-y-auto">
          {/* Title plate. Wide, shallow, inset nameplate. The plate is
              carved into the view via a multi-layer inset box-shadow;
              a dark radial gradient pulls the center into shadow so the
              text sits in a "well"; and the Viking ornament is both
              embossed (mix-blend-mode: overlay) AND textured with the
              project's shared tex-noise grain so it reads as engraved,
              weathered metalwork rather than a flat stamp. */}
          <motion.div className="flex justify-center pt-6 pb-2 shrink-0" {...screenPanel(1)}>
            {/* Outer plate. Border, background, inset shadow. NO
                overflow-hidden here so FourCorners (which sits at
                -2px offsets from the edges) can render past the
                border without being clipped. */}
            <div
              data-tutorial="trials-xp-summary"
              className="relative rounded-xl text-center"
              style={{
                background: 'linear-gradient(180deg, rgba(20,16,10,0.94) 0%, rgba(8,6,4,0.98) 100%)',
                border: `1px solid ${GOLD} 0.32)`,
                boxShadow: `
                  inset 0 6px 18px rgba(0,0,0,0.8),
                  inset 0 2px 4px rgba(0,0,0,0.6),
                  inset 0 -1px 0 ${GOLD} 0.1),
                  0 4px 14px rgba(0,0,0,0.45),
                  0 0 22px rgba(180,140,60,0.06)
                `,
                minWidth: '52cqw',
                padding: '14px 64px 16px',
                isolation: 'isolate',
              }}
            >
              {/* Inner clip wrapper. Contains the ornament and depth
                  shadow layers. overflow-hidden here (instead of on
                  the outer plate) so the decorative layers clip to
                  the rounded corners WITHOUT clipping the corner
                  plating brackets that need to sit at the edges. */}
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none"
              >
                {/* Base ornament. Flat gold at 0.05 so it sits very
                    subtly in the background. Uses VikingOrnament so
                    the URL gets the per-session cache-bust (CEF and
                    browsers aggressively cache CSS mask-image URLs).
                    The style override fills the whole clip wrapper
                    with mask-size: cover instead of the centerpiece
                    variant's default 72%-width + contain sizing. */}
                <VikingOrnament
                  ornament="style2c005"
                  variant="centerpiece"
                  color="rgba(212, 168, 67, 1)"
                  opacity={0.05}
                  style={{
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    transform: 'none',
                    maskSize: 'cover',
                    WebkitMaskSize: 'cover',
                  }}
                />

                {/* Circular depth shadow. Sits ABOVE the ornament so
                    it darkens the ornament in the center where the
                    title lives, producing the "text in a shadow well"
                    look. 320px circle covers the full text width and
                    fades cleanly to transparent before the corners. */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(circle 320px at center, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.5) 30%, rgba(0,0,0,0.18) 65%, transparent 100%)',
                  }}
                />
              </div>

              {/* Corner plating. Siblings of the clip wrapper, not
                  children, so overflow-hidden doesn't clip their
                  -2px edge offsets. Radius 14 (plate radius 12 + 2px
                  offset) makes the bracket curves concentric with the
                  plate's rounded-xl corners so they hug the edge
                  cleanly instead of sitting slightly inside it. */}
              <FourCorners />

              <h1 className="relative text-[1.625cqw] leading-tight font-bold arena-heading tracking-wide" style={{
                color: TEXT_PRIMARY,
                textShadow: '0 2px 10px rgba(0,0,0,0.92), 0 0 28px rgba(180,140,60,0.22)',
              }}>
                {season.name}
              </h1>
              <div className="relative text-[0.625cqw] mt-1" style={{ color: TEXT_MUTED }}>
                {timeLeft.expired
                  ? t('Season ended')
                  : t('{d}d {h}h remaining', { d: timeLeft.days, h: timeLeft.hours })}
              </div>

              {/* Seasonal stats row. Sits at the bottom of the title
                  plate as a single hub for both Arcane Trials progress
                  and the Ranked Season ladder. Three slots: season XP
                  on the left, ranked points + qualification gauge in
                  the middle, leaderboard CTA on the right. The pair of
                  thin gold dividers between the slots ties them
                  visually into one composed control instead of three
                  detached widgets. */}
              <div
                className="relative mt-3 pt-3 grid items-center gap-4"
                style={{
                  // Three equal-width slots split by thin gold dividers.
                  // Each slot centers its own content, so the visual
                  // rhythm stays consistent regardless of which slot
                  // happens to have wider content this frame.
                  gridTemplateColumns: '1fr auto 1fr auto 1fr',
                  borderTop: `1px solid ${GOLD} 0.18)`,
                }}
              >
                <TrialsStatBlock
                  label={t('Season XP')}
                  value={seasonXp.toLocaleString()}
                  caption={t('Tier {n} / {total}', { n: currentLevel, total: season.tiers.length })}
                />
                <TrialsStatDivider />
                <TrialsRankedBlock rankedSelf={this.state.rankedSelf} />
                <TrialsStatDivider />
                <div className="flex justify-center">
                  <TrialsLeaderboardButton onOpen={this.handleOpenRankedSeason} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Body. Reward track centered, quests pinned to bottom */}
          <div className="flex-1 flex flex-col min-h-0">

            {/* Reward Track. Vertically centered in available space.
                One shared container holds the header row, the reward
                track row, and the progress bar with a uniform horizontal
                padding. Every tier-positioned element (reward circles,
                bar ticks, XP labels) uses the SAME xp-based coordinate
                system: left: ${(xpRequired / maxXp) * 100}% with a
                translateX(-50%) centering transform. This makes the
                visual layout match the fill math exactly. Tier 1 at
                150 XP sits at 4.2% of the bar, NOT at 0% just because
                it's the first in the array. */}
            <motion.div className="flex-1 flex items-center justify-center px-6" {...screenPanel(2)}>
            <div className="w-full" style={{ paddingLeft: 72, paddingRight: 72 }}>
              <div className="flex items-center justify-end mb-4">
                {nextTier ? (() => {
                  const [before, after] = t('Next reward in {xp}').split('{xp}');
                  return (
                    <div className="text-xs" style={{ color: TEXT_MUTED }}>
                      {before}<span style={{ color: ACCENT_GOLD }}>{nextTier.xpRemaining} XP</span>{after}
                    </div>
                  );
                })() : (
                  <div className="text-xs" style={{ color: ACCENT_GOLD }}>{t('All rewards unlocked!')}</div>
                )}
              </div>

              {/* Rewards row. Absolute-positioned tier cards anchored
                  at each tier's xp percentage. The container is a fixed
                  240px tall so foil cards (the tallest items) have room,
                  and each tier is centered on its target percentage via
                  translateX(-50%). */}
              <div data-tutorial="trials-reward-track" className="relative" style={{ height: '15cqw' }}>
                {season.tiers.map((tier, idx) => {
                  const { reward } = tier;
                  const isClaimed = progress.claimedTiers.includes(tier.level);
                  const unlocked = seasonXp >= tier.xpRequired;
                  const claimable = canClaimTier(tier.level, seasonXp, progress.claimedTiers, season.tiers);
                  const isFoil = isFoilTier(tier);
                  const locked = !unlocked && !isClaimed;
                  const isClaimingThis = this.state.claiming === tier.level;
                  const xpPct = (tier.xpRequired / maxXp) * 100;

                  return (
                    <div
                      key={tier.level}
                      className="absolute bottom-0 flex flex-col items-center"
                      style={{
                        left: `${xpPct}%`,
                        transform: 'translateX(-50%)',
                        width: '7.5cqw',
                      }}
                    >
                      <div
                        className="relative w-full transition-all duration-300"
                        style={{
                          // Claimed tiers stay at full opacity now. The
                          // pulsing glow on the button is what marks them
                          // as "achieved" instead of dimming, which made
                          // them look retired/locked.
                          transform: claimable ? 'scale(1.04) translateY(-4px)' : 'scale(1)',
                        }}
                      >
                        {isFoil ? (() => {
                          const foilReward = this.resolveFoilReward(reward);
                          if (!foilReward) return <div className="aspect-[63/88] rounded-card" style={{ background: PANEL_BG }} />;
                          const { card, printing } = foilReward;

                          // Some foil tiers also pay out coins (tier 10
                          // gives 500 + the Elite). Render the coin amount as
                          // a circular badge clipped to the foil tile's
                          // bottom-right corner so the player sees both
                          // rewards at a glance. Same vocabulary as
                          // the arcana medallions.
                          const coinReward = Number.isInteger(reward.coins) ? reward.coins : 0;
                          return (
                            <div className="relative w-full">
                              <DeckCardTile
                                entry={{ card, printing, zone: 'spellbook', entryIndex: 0 }}
                                isSelected={false}
                                liveFoil={this.state.foilLive}
                                onClick={() => claimable && this.handleClaim(tier.level)}
                                onHoverChange={(hovered) => this.setState({ hoveredCard: hovered ? { card, printing } : null })}
                              />
                              {coinReward > 0 ? (
                                <div
                                  className="absolute flex items-center justify-center gap-0.5 z-10 tabular-nums pointer-events-none"
                                  style={{
                                    minWidth: '1.875cqw',
                                    height: '1.125cqw',
                                    padding: '0 5px',
                                    bottom: -4,
                                    right: -4,
                                    borderRadius: 'var(--radius-lg)',
                                    background: 'linear-gradient(180deg, rgba(40,30,12,0.97) 0%, rgba(20,15,6,0.97) 100%)',
                                    border: `1.5px solid ${GOLD} 0.55)`,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,220,140,0.15)',
                                    color: COIN_COLOR,
                                    fontSize: '0.625cqw',
                                    fontWeight: 700,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                                    lineHeight: 1,
                                  }}
                                  title={t('+{n} coins', { n: coinReward })}
                                >
                                  <CoinIcon size={11} glow={false} />
                                  {coinReward}
                                </div>
                              ) : null}
                              {isClaimed ? this.renderClaimedCheck() : null}
                              {this.trials && !isClaimed ? (
                                <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ bottom: '-1.125cqw' }}>
                                  <ForgedButton
                                    className="px-2 py-0.5 text-[0.5cqw] whitespace-nowrap"
                                    disabled={this.state.rerolling != null || this.trials.ferocity < FOIL_REROLL_COST}
                                    onClick={(e) => { e.stopPropagation(); this.handleRerollFoil(tier.level); }}
                                  >
                                    {t('Re-roll ({n} Ferocity)', { n: FOIL_REROLL_COST })}
                                  </ForgedButton>
                                </div>
                              ) : null}
                            </div>
                          );
                        })() : (() => {
                          const hasShards = reward.arcanaShards > 0;
                          // Tiers that grant Arcana Shards make shards the
                          // headline reward. Bigger glyph, cyan tint.
                          // and demote the coin amount to a small corner
                          // badge. Plain gold tiers keep the original
                          // layout (coin number centered in the circle).
                          return (
                            <div className="flex justify-center">
                              {/* Anchor wrapper sized to the button so any
                                  corner badges position relative to the
                                  circle itself, not the full-width tier
                                  column above. */}
                              <div className="relative" style={{ width: '3.75cqw', height: '3.75cqw' }}>
                                <button
                                  type="button"
                                  className={cn(
                                    'absolute inset-0 flex items-center justify-center transition-all duration-300 overflow-hidden',
                                    isClaimed && (hasShards ? 'tier-claimed-glow-shard' : 'tier-claimed-glow-gold'),
                                  )}
                                  style={{
                                    borderRadius: '50%',
                                    background: 'rgba(20,16,10,0.85)',
                                    border: claimable
                                      ? `2px solid ${hasShards ? 'rgba(125,211,252,0.7)' : `${GOLD} 0.6)`}`
                                      : isClaimed
                                        ? `2px solid ${hasShards ? 'rgba(125,211,252,0.55)' : `${GOLD} 0.55)`}`
                                        : `1px solid ${hasShards ? 'rgba(125,211,252,0.18)' : `${GOLD} 0.12)`}`,
                                    // Inline boxShadow only for the claimable
                                    // halo. Claimed circles use the keyframe
                                    // animation class above; locked circles
                                    // get nothing.
                                    boxShadow: claimable
                                      ? hasShards
                                        ? '0 0 16px rgba(125,211,252,0.45)'
                                        : '0 0 16px rgba(212,168,67,0.35)'
                                      : undefined,
                                    cursor: claimable ? 'pointer' : 'default',
                                    isolation: 'isolate',
                                  }}
                                  data-sound={claimable ? UI.CONFIRM : undefined}
                                  disabled={!claimable || isClaimingThis}
                                  onClick={() => claimable && this.handleClaim(tier.level)}
                                >
                                  <VikingOrnament
                                    ornament="style2d007"
                                    variant="medallion"
                                    color={hasShards ? 'rgba(125, 211, 252, 0.9)' : 'rgba(232, 200, 130, 0.9)'}
                                    opacity={claimable ? 0.85 : 0.55}
                                    style={{
                                      maskSize: '128%',
                                      WebkitMaskSize: '128%',
                                    }}
                                  />
                                  {hasShards ? (
                                    <div className="relative flex items-center gap-0.5" style={{ color: '#7dd3fc', textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 0 8px rgba(0,0,0,0.6)' }}>
                                      <ShardIcon size={11} />
                                      <span className="text-base font-bold tabular-nums leading-none">{reward.arcanaShards}</span>
                                    </div>
                                  ) : (
                                    <div className="relative flex items-center gap-1 text-base font-bold" style={{ color: '#f0d060', textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.55)' }}>
                                      <CoinIcon size={14} />
                                      {reward.coins}
                                    </div>
                                  )}
                                  {isClaimingThis ? <div className="absolute inset-0 flex items-center justify-center rounded-full" style={{ background: 'rgba(0,0,0,0.5)' }}><RuneSpinner size={16} /></div> : null}
                                </button>
                                {/* Circular coin badge. Anchored to the
                                    bottom-right of the medallion itself so
                                    it overlaps the rim. Pure circle, not
                                    a pill, holding the coin amount. */}
                                {hasShards ? (
                                  <div
                                    className="absolute flex items-center justify-center gap-0.5 z-10 tabular-nums"
                                    style={{
                                      minWidth: '1.625cqw',
                                      height: '1cqw',
                                      padding: '0 4px',
                                      bottom: -4,
                                      right: -4,
                                      borderRadius: 'var(--radius-lg)',
                                      background: 'linear-gradient(180deg, rgba(40,30,12,0.97) 0%, rgba(20,15,6,0.97) 100%)',
                                      border: `1.5px solid ${GOLD} 0.55)`,
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,220,140,0.15)',
                                      color: COIN_COLOR,
                                      fontSize: '0.5625cqw',
                                      fontWeight: 700,
                                      textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                                      lineHeight: 1,
                                    }}
                                    title={t('+{n} coins', { n: reward.coins })}
                                  >
                                    <CoinIcon size={10} glow={false} />
                                    {reward.coins}
                                  </div>
                                ) : null}
                                {isClaimed ? this.renderClaimedCheck() : null}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Labels below reward */}
                      <div className="text-[0.625cqw] font-bold mt-1.5" style={{ color: unlocked ? ACCENT_GOLD : TEXT_MUTED }}>
                        {t('Tier {n}', { n: tier.level })}
                      </div>
                      {isFoil && reward.foilRarity ? (
                        <div className="text-center mt-0.5">
                          <span className="text-[0.5cqw] font-bold px-1.5 py-0.5 rounded" style={{
                            background: 'rgba(0,0,0,0.5)',
                            color: RARITY_COLORS[reward.foilRarity] || TEXT_MUTED,
                          }}>
                            {reward.foilRarity === 'Unique' ? `✦ ${reward.foilRarity}` : `◆ ${reward.foilRarity}`}
                          </span>
                        </div>
                      ) : null}
                      {claimable && !isClaimingThis ? (
                        <button
                          type="button"
                          className="mt-1 px-2.5 py-0.5 text-[0.5625cqw] font-bold"
                          style={{ ...BEVELED_BTN, color: ACCENT_GOLD, borderRadius: 'var(--radius-sm)', background: `${GOLD} 0.15)`, border: `1px solid ${GOLD} 0.35)` }}
                          data-sound={UI.CONFIRM}
                          onClick={() => this.handleClaim(tier.level)}
                        >
                          {t('Claim')}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {/* Progress bar. Inherits the shared padding from the
                  w-full wrapper above so the bar's 0–100% range lines
                  up perfectly with the reward row's absolute XP-based
                  positioning. No extra padding override here. */}
              <div className="mt-5">
                <div className="relative h-6 rounded-lg overflow-hidden" style={{
                  background: 'rgba(10,8,6,0.7)',
                  border: `1px solid ${GOLD} 0.18)`,
                  boxShadow: `inset 0 2px 6px rgba(0,0,0,0.5), 0 1px 0 ${GOLD} 0.06)`,
                }}>
                  {/* Fill. Liquid gold with animated sheen + glitter
                      via .progress-fill-liquid-gold (defined in app.css).
                      Multi-period drifting bright spot (9 s) + glitter
                      field drift (7 s) + layered vertical molten gold
                      gradient. Simple static gold border at the right
                      edge while the bar is partially full. */}
                  <div className="absolute inset-y-0 left-0 rounded-lg progress-fill-liquid-gold" style={{
                    width: `${Math.min(100, (seasonXp / maxXp) * 100)}%`,
                    borderRight: seasonXp > 0 && seasonXp < maxXp ? `2px solid ${GOLD} 0.55)` : 'none',
                    transition: 'width 0.5s ease-out',
                  }} />

                  {/* Minor tick marks. 3 subticks BETWEEN each pair of
                      adjacent tiers, so every segment on the bar is
                      visually divided into quarters. Because tiers are
                      non-uniformly spaced in XP, each segment's subticks
                      also vary in spacing, which correctly reflects the
                      XP curve. The tick's XP value is interpolated
                      between the two surrounding tier thresholds. */}
                  {season.tiers.slice(0, -1).flatMap((tier, idx) => {
                    const nextTier = season.tiers[idx + 1];
                    const segments = 4; // 3 subticks between each tier
                    return Array.from({ length: segments - 1 }, (_, s) => {
                      const frac = (s + 1) / segments;
                      const xpAt = tier.xpRequired + (nextTier.xpRequired - tier.xpRequired) * frac;
                      const pct = (xpAt / maxXp) * 100;
                      const reached = seasonXp >= xpAt;
                      return (
                        <div
                          key={`minor-${tier.level}-${s}`}
                          className="absolute -translate-x-1/2"
                          style={{
                            left: `${pct}%`,
                            top: '22%',
                            bottom: '22%',
                            width: '0.0625cqw',
                            background: reached ? `${GOLD} 0.28)` : `${GOLD} 0.08)`,
                          }}
                        />
                      );
                    });
                  })}
                  {/* Major tier checkpoint marks. Positioned at each
                      tier's actual XP percentage so they line up with
                      both the reward circles above and the bar fill. */}
                  {season.tiers.map((tier) => {
                    const pct = (tier.xpRequired / maxXp) * 100;
                    const reached = seasonXp >= tier.xpRequired;
                    return (
                      <div key={`major-${tier.level}`} className="absolute -translate-x-1/2" style={{
                        left: `${pct}%`,
                        top: '10%',
                        bottom: '10%',
                        width: '0.125cqw',
                        background: reached ? `${GOLD} 0.5)` : `${GOLD} 0.15)`,
                        borderRadius: 'var(--radius-xs)',
                      }} />
                    );
                  })}

                  {/* XP text centered in the bar */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[0.6875cqw] font-bold" style={{ color: TEXT_PRIMARY, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                      {t('{xp} / {max} XP', { xp: seasonXp.toLocaleString(), max: maxXp.toLocaleString() })}
                    </span>
                  </div>
                </div>

                {/* XP threshold row. Each label sits centered under
                    its corresponding tier tick using the SAME xp-based
                    percentage math as the tick markers and the reward
                    circles above. */}
                <div className="relative h-4 mt-1.5">
                  {season.tiers.map((tier) => {
                    const pct = (tier.xpRequired / maxXp) * 100;
                    const reached = seasonXp >= tier.xpRequired;
                    return (
                      <span
                        key={`xplabel-${tier.level}`}
                        className="absolute -translate-x-1/2 text-[0.5625cqw] font-semibold tabular-nums whitespace-nowrap"
                        style={{
                          left: `${pct}%`,
                          top: 0,
                          color: reached ? `${GOLD} 0.45)` : `${GOLD} 0.75)`,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {tier.xpRequired.toLocaleString()}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            </motion.div>
            </div>

            {/* Quest Footer. When the player has the skill-tree perk
                that bumps the active quest pool from 3 → 6 we render
                all six on a single row using a wider container and a
                slimmer card variant, so the ornamental divider above
                stays where it belongs instead of getting pushed up
                into the season progress bar. */}
            <motion.div data-tutorial="trials-quests" className="shrink-0 px-6 pb-5 pt-3" style={{ borderTop: `1px solid ${GOLD} 0.1)` }} {...screenPanel(3)}>
              <OrnamentalDivider className={cn('w-full mx-auto mb-3', questsCompact ? 'max-w-[80cqw]' : 'max-w-[56cqw]')} />
              <div className={cn('w-full mx-auto', questsCompact ? 'max-w-[80cqw]' : 'max-w-[56cqw]')}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold arena-heading" style={{
                    color: TEXT_PRIMARY,
                    textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                  }}>
                    {t('Active Quests')}
                  </h2>
                  {this.trials ? <FerocityGauge value={this.trials.ferocity} max={this.trials.max} width={18} size={0.85} /> : null}
                </div>
                <div className={questsCompact ? 'grid grid-cols-6 gap-3' : 'grid grid-cols-3 gap-4'}>
                  {activeQuests.map((q) => this.renderQuestCard(q, questsCompact))}
              </div>
            </div>
          </motion.div>
        </div>
        {this.state.inspectedEntry ? (
          <CardInspector
            card={this.state.inspectedEntry.card}
            imageUrl={this.state.inspectedEntry.printing?.image_url}
            rarity={this.state.inspectedEntry.card?.rarity}
            foiling={this.state.inspectedEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedEntry: null })}
          />
        ) : null}

        {/* First-run Arcane Trials tutorial. */}
        {this.state.showTrialsTutorial && (
          <TutorialOverlay
            steps={trialsTutorialSteps()}
            onDismiss={this.handleTrialsTutorialDismiss}
          />
        )}
      </div>
    );
  }
}

// ── Title-plate stats row helpers ─────────────────────────────────
// The three helpers below compose the new seasonal hub embedded at
// the bottom of the title plate. Each one renders one "slot" inside
// the grid laid out by the parent: a labelled stat block, a thin gold
// divider between slots, the ranked stat / qualification gauge, and
// the leaderboard CTA. Keeping them small and focused means the plate
// markup reads as a vertical stack of named regions instead of a
// nested mess of inline JSX.

function TrialsStatBlock({ label, value, caption, valueColor = ACCENT_GOLD }) {
  return (
    <div className="flex flex-col items-center gap-0.5 leading-none">
      <span className="text-[0.5625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
        {label}
      </span>
      <span
        className="text-[1.375cqw] font-bold tabular-nums arena-heading"
        style={{ color: valueColor, textShadow: '0 2px 10px rgba(0,0,0,0.9), 0 0 14px rgba(212,168,67,0.18)' }}
      >
        {value}
      </span>
      {caption ? (
        <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
          {caption}
        </span>
      ) : null}
    </div>
  );
}

function TrialsStatDivider() {
  return (
    <div
      aria-hidden="true"
      className="self-stretch w-px mx-1"
      style={{
        background: `linear-gradient(180deg, transparent 0%, ${GOLD} 0.4) 50%, transparent 100%)`,
      }}
    />
  );
}

// Ranked stat slot. Shows points + rank when the player is qualified,
// OR points + a linear qualification gauge while they're still under
// the match-count threshold. The gauge swaps in instead of the rank
// caption so the slot's footprint stays roughly constant between the
// two states.
function TrialsRankedBlock({ rankedSelf }) {
  const eligible = !!rankedSelf?.eligible;
  const rank = rankedSelf?.rank;
  const points = rankedSelf?.points ?? 0;
  const matchesPlayed = rankedSelf?.matchesPlayed ?? 0;
  const matchesUntilEligible = rankedSelf?.matchesUntilEligible ?? 10;
  const threshold = matchesPlayed + matchesUntilEligible;
  const pointsColor = points >= 0 ? ACCENT_GOLD : '#f87171';
  const [rankBefore, rankAfter] = t('Rank {rank}').split('{rank}');

  return (
    <div className="flex flex-col items-center gap-0.5 leading-none min-w-[10cqw]">
      <span className="text-[0.5625cqw] font-semibold" style={{ color: TEXT_MUTED }}>
        {t('Valor Points')}
      </span>
      <span
        className="text-[1.375cqw] font-bold tabular-nums arena-heading"
        style={{ color: pointsColor, textShadow: '0 2px 10px rgba(0,0,0,0.9), 0 0 14px rgba(212,168,67,0.18)' }}
      >
        {points > 0 ? `+${points}` : points}
      </span>
      {eligible && rank != null ? (
        <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
          {rankBefore}<span style={{ color: TEXT_BODY }}>#{rank.toLocaleString()}</span>{rankAfter}
        </span>
      ) : (
        <TrialsQualifyGauge matches={matchesPlayed} threshold={threshold} />
      )}
    </div>
  );
}

// Linear qualification gauge. Same visual language as the inline
// gauge inside the Ranked overlay's Your Season panel. Recessed track
// with a warm-gold fill, an "X / 10" tabular-numeric label sitting
// inside the row for compactness.
function TrialsQualifyGauge({ matches, threshold }) {
  const pct = threshold > 0 ? Math.max(0, Math.min(100, Math.round((matches / threshold) * 100))) : 0;
  return (
    <div className="flex flex-col items-stretch gap-1 mt-1 w-full">
      <div className="flex items-center gap-2 w-full">
        <div
          className="relative flex-1 h-[0.375cqw] overflow-hidden"
          style={{
            background: 'rgba(0,0,0,0.6)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(180,140,60,0.2)',
          }}
        >
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(180deg, rgba(255,230,160,0.9) 0%, rgba(212,168,67,0.95) 50%, rgba(150,110,40,0.9) 100%)`,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 8px rgba(212,168,67,0.4)',
              borderRadius: 'var(--radius-sm)',
              transition: 'width 0.4s ease',
            }}
          />
        </div>
        <span
          className="text-[0.5625cqw] tabular-nums shrink-0"
          style={{ color: ACCENT_GOLD }}
        >
          {matches}/{threshold}
        </span>
      </div>
      <span
        className="text-[0.5cqw] text-center"
        style={{ color: TEXT_MUTED }}
      >
        {t('Matches to qualify')}
      </span>
    </div>
  );
}

// Leaderboard CTA. Primary action button. Borrows the carved-stone
// recipe used by FirstPlayerDiceModal + ArenaStarterPicker (the project's
// textured-panel look): stone / chisel / scratches / cracks blended on
// top of a dark gold-tinted base, gold outer rim, layered shadow stack
// for the outer drop + halo + inset rim + inset vignette. Opaque all
// the way through. No transparencies. So it reads as a solid tablet
// the player can press rather than a translucent overlay.
const LEADERBOARD_BTN_BASE = {
  position: 'relative',
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(48,36,16,1) 0%, rgba(28,20,8,1) 100%)`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${GOLD} 0.6)`,
 borderRadius: 'var(--radius-md)',
 color: ACCENT_GOLD,
 fontSize: '0.6875cqw',
 fontWeight: 700,
 boxShadow: [
 'inset 0 1px 0 rgba(255,235,195,0.12)',
 `inset 0 0 0 1px ${GOLD} 0.22)`,
    'inset 0 -2px 6px rgba(0,0,0,0.5)',
    'inset 0 0 28px rgba(0,0,0,0.5)',
    '0 4px 12px rgba(0,0,0,0.5)',
    `0 0 22px ${GOLD} 0.18)`,
  ].join(', '),
  textShadow: '0 1px 0 rgba(0,0,0,0.7), 0 0 10px rgba(212,168,67,0.25)',
  isolation: 'isolate',
};
const LEADERBOARD_BTN_HOVER = {
  ...LEADERBOARD_BTN_BASE,
  background: `${CONTENT_BG_DEFAULT}, linear-gradient(180deg, rgba(72,54,22,1) 0%, rgba(40,28,10,1) 100%)`,
  borderColor: `${GOLD} 0.85)`,
  boxShadow: [
    'inset 0 1px 0 rgba(255,235,195,0.18)',
    `inset 0 0 0 1px ${GOLD} 0.35)`,
    'inset 0 -2px 6px rgba(0,0,0,0.5)',
    'inset 0 0 28px rgba(0,0,0,0.45)',
    '0 4px 12px rgba(0,0,0,0.55)',
    `0 0 30px ${GOLD} 0.28)`,
  ].join(', '),
};

function TrialsLeaderboardButton({ onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={t('Open the bi-weekly Ranked Season standings')}
      className="group inline-flex items-center gap-2 px-4 py-2.5 transition-colors cursor-pointer"
      style={LEADERBOARD_BTN_BASE}
      onMouseEnter={(e) => { Object.assign(e.currentTarget.style, LEADERBOARD_BTN_HOVER); }}
      onMouseLeave={(e) => { Object.assign(e.currentTarget.style, LEADERBOARD_BTN_BASE); }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ width: '0.875cqw', height: '0.875cqw' }}
      >
        <path d="M6 9H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2" />
        <path d="M18 9h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
        <path d="M6 3h12v6a6 6 0 0 1-12 0V3z" />
        <path d="M9 21h6" />
        <path d="M12 17v4" />
      </svg>
      <span>{t('Season Standings')}</span>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="transition-transform group-hover:translate-x-0.5"
        style={{ width: '0.75cqw', height: '0.75cqw' }}
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}
